import Flickity from 'flickity';

/**
 * Store Details
 * @param {object} opts
 */
function storeDetailsPage(opts) {
  const config = {
    dom: {
      carousel: 'js-store-details-carousel',
      wrapper: 'js-store-details-carousel-wrapper',
      storeAddress: 'js-store-address',
      storeContact: 'js-store-contact',
      storeDetails: 'js-store-details',
      storeDirections: 'js-store-directions',
      storeName: 'js-store-name',
      storePhone: 'js-store-phone',
      storeEmail: 'js-store-email',
      yextStoreAddress: 'js-yext-store-address',
      yextStorePhone: 'js-yext-store-phone',
      optionalComa: 'js-optional-coma',
    },
    cls: {
      active: 'is-active',
      hide: 'hide',
    },
  };
  const c = __.extend(config, opts);

  const flickityOpts = {
    contain: true,
    draggable: true,
    pageDots: true,
    prevNextButtons: true,
    wrapAround: true,
  };

  // global elements
  const $wrappers = document.body.querySelectorAll(`.${c.dom.wrapper}`);
  const $storeName = document.body.querySelector(`.${c.dom.storeName}`);
  const flickityCarousels = [];

  // Data
  const widgetTag = SDG.Data.stockistWidgetTag || null;
  const storage = window.sessionStorage;
  let view;

  /**
   * Init
   */
  function init() {
    if ($storeName) {
      getStoreData($storeName);
    }

    onWindowResize();
    __.windowResize(onWindowResize);
  }

  /**
   * On Window Resize
   */
  function onWindowResize() {
    // desktop
    __.mq({
      view: 'desktop',
      callback: () => {
        if (view !== 'desktop') {
          view = 'desktop';
          rebuildFlickity();
        }
      },
    });

    // mobile
    __.mq({
      view: 'mobile',
      callback: () => {
        if (view !== 'mobile') {
          view = 'mobile';
          rebuildFlickity();
        }
      },
    });
  }

  /**
   * rebuild flickity
   */
  function rebuildFlickity() {
    if (!$wrappers?.length) return;

    [...$wrappers].forEach(($wrapper, index) => {
      const activeCarousel = flickityCarousels[index];
      if (activeCarousel) activeCarousel.destroy();
      const $carousel = $wrapper.querySelector(`.${c.dom.carousel}`);

      if (!$carousel) return;

      setTimeout(() => {
        const flickityCarousel = new Flickity($carousel, flickityOpts);

        if (activeCarousel) {
          flickityCarousels[index] = flickityCarousel;
        } else {
          flickityCarousels.push(flickityCarousel);
        }
      }, 500); // short timeout for Flickity cell sizing
    });
  }

  /**
   * Get store data
   * @param {HTMLElement} $el store name element
   */
  async function getStoreData($el) {
    const allStoresData = storage
      ? storage.getItem('allStores')
      : null;
    const { storeName } = $el.dataset;
    const allStores = allStoresData
      ? JSON.parse(allStoresData)
      : null;
    let store;

    if (allStores) {
      store = allStores.find((s) => s.name === storeName);

      if (store) {
        buildStoreData(store);
      }
    } else {
      const results = await fetch(`https://stockist.co/api/v1/${widgetTag}/locations/all`)
        .then((searchResult) => searchResult.json())
        .catch((err) => console.error(err));

      if (results) storage.setItem('allStores', JSON.stringify(results));
      store = results.find((s) => s.name === storeName);

      if (store) {
        buildStoreData(store);
      }
    }
  }

  /**
   * Set store data
   * @param {Object} data store data
   */
  function buildStoreData(data) {
    const $storeDetails = document.querySelector(`.${c.dom.storeDetails}`);
    const {
      address_line_1: addressLine1,
      city,
      state,
      phone,
      postal_code: postalCode,
      email,
    } = data;
    const storeParameters = `${addressLine1},+${city},+${state}`;
    const googleMapsUrl = `https://www.google.com/maps/dir//${storeParameters}`;
    const addressLine2 = data.address_line_2
      ? `${data.address_line_2}<br>`
      : '';

    // Set address data
    setTimeout(() => {
      const $yextStoreAddress = document.querySelector(`.${c.dom.yextStoreAddress}`);
      const $optionalComa = document.querySelector(`.${c.dom.optionalComa}`);
      if (!$yextStoreAddress || ($yextStoreAddress && $yextStoreAddress.innerHTML === '')) {
        __.addClass($optionalComa, c.cls.hide);
        const $address = $storeDetails.querySelector(`.${c.dom.storeAddress}`);
        $address.innerHTML = `
          ${addressLine1}<br>
          ${addressLine2}
          ${city}, ${state} ${postalCode}
        `;
        observeYextAddress($yextStoreAddress, $address);
      } else {
        __.removeClass($optionalComa, c.cls.hide);
      }
    }, 100);

    // Set contact data
    const $contact = $storeDetails.querySelector(`.${c.dom.storeContact}`);
    const $yextStorePhone = $contact.querySelector(`.${c.dom.yextStorePhone}`);
    const $phone = $contact.querySelector(`.${c.dom.storePhone}`);

    if ($yextStorePhone && $yextStorePhone.innerHTML !== '') {
      const formattedPhone = reFormatUSPhoneNumber($yextStorePhone.innerHTML);
      $yextStorePhone.title = formattedPhone;
      $yextStorePhone.innerHTML = formattedPhone;
    } else if (phone) {
      const formattedPhone = reFormatUSPhoneNumber(phone);
      $phone.href = `tel:${formattedPhone}`;
      $phone.title = formattedPhone;
      $phone.innerHTML = formattedPhone;
      observeYextPhoneNumber($yextStorePhone, $phone);
    }

    // Set email data
    const $email = $contact.querySelector(`.${c.dom.storeEmail}`);
    if (email) {
      $email.href = `mailto:${email}`;
      $email.title = email;
      $email.innerHTML = email;
    }

    // Set direction data
    const $directions = $storeDetails.querySelector(`.${c.dom.storeDirections}`);
    $directions.href = googleMapsUrl;
    $directions.title = googleMapsUrl;
  }

  /**
   * Reformat phone number
   * @param {String} phoneNumber phone number
   */
  function reFormatUSPhoneNumber(phoneNumber) {
    const reg = (phoneNumber).replace(/\D+/g, '').match(/^(\d{3})(\d{3})(\d{4})$/);

    if (reg) {
      return `+ 1 (${reg[1]}) ${reg[2]}-${reg[3]}`;
    }

    return null;
  }

  /**
   * Observe Yext Address
   * @param {HTMLElement} yextStoreAddress yext store address
   * @param {HTMLElement} defaultAddress default address
   */
  function observeYextAddress(yextStoreAddress, defaultAddress) {
    if (!yextStoreAddress) return;

    const observer = new MutationObserver(() => {
      if (yextStoreAddress.innerHTML !== '') {
        __.addClass(defaultAddress, `${c.cls.hide}`);
        observer.disconnect();
      }
    });

    observer.observe(yextStoreAddress, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Disconnect observer after 30 seconds
    setTimeout(() => observer.disconnect(), 30000);
  }

  /**
   * Observe Yext Phone Number
   * @param {HTMLElement} yextStorePhone yext store phone
   * @param {HTMLElement} defaultPhone default phone
   */
  function observeYextPhoneNumber(yextStorePhone, defaultPhone) {
    if (!yextStorePhone) return;

    const observer = new MutationObserver(() => {
      if (yextStorePhone.innerHTML !== '') {
        const formattedPhone = reFormatUSPhoneNumber(yextStorePhone.innerHTML);
        yextStorePhone.title = formattedPhone;
        yextStorePhone.innerHTML = formattedPhone;
        __.addClass(defaultPhone, `${c.cls.hide}`);
        observer.disconnect();
      }
    });

    observer.observe(yextStorePhone, {
      childList: true,
      attributes: true,
    });

    // Disconnect observer after 30 seconds
    setTimeout(() => observer.disconnect(), 30000);
  }

  return init();
}

export default storeDetailsPage;
